import React, { useEffect, useState, useMemo } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Shield, ShieldAlert, CheckCircle2, XCircle, RefreshCw, Filter } from "lucide-react";
import { toast } from "sonner";

const EVENT_LABELS = {
  login_success: { label: "Login correcto", color: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  login_failed: { label: "Login fallido", color: "bg-red-100 text-red-700 border-red-200" },
  account_locked: { label: "Cuenta bloqueada", color: "bg-orange-100 text-orange-700 border-orange-200" },
  account_unlocked: { label: "Cuenta desbloqueada", color: "bg-blue-100 text-blue-700 border-blue-200" },
  password_changed: { label: "Contraseña cambiada", color: "bg-slate-100 text-slate-700 border-slate-200" },
  password_reset_requested: { label: "Reset solicitado", color: "bg-amber-100 text-amber-700 border-amber-200" },
  password_reset_completed: { label: "Reset completado", color: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  user_created: { label: "Usuario creado", color: "bg-blue-100 text-blue-700 border-blue-200" },
  user_role_changed: { label: "Cambio de rol", color: "bg-purple-100 text-purple-700 border-purple-200" },
  user_status_changed: { label: "Cambio de estado", color: "bg-purple-100 text-purple-700 border-purple-200" },
  user_deleted: { label: "Usuario eliminado", color: "bg-red-100 text-red-700 border-red-200" },
  temp_password_generated: { label: "Contraseña temporal", color: "bg-amber-100 text-amber-700 border-amber-200" },
  activation_link_sent: { label: "Enlace activación", color: "bg-blue-100 text-blue-700 border-blue-200" },
  must_change_password_enforced: { label: "Cambio obligatorio", color: "bg-orange-100 text-orange-700 border-orange-200" },
};

const labelFor = (t) => EVENT_LABELS[t] || { label: t, color: "bg-slate-100 text-slate-600 border-slate-200" };

export default function SecurityEventsPage() {
  const { axiosAuth } = useAuth();
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filtros
  const [fEmail, setFEmail] = useState("");
  const [fType, setFType] = useState("");
  const [fSuccess, setFSuccess] = useState("any"); // any | true | false
  const [fIp, setFIp] = useState("");
  const [fFrom, setFFrom] = useState("");
  const [fTo, setFTo] = useState("");

  const load = async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (fEmail) params.append("email", fEmail);
    if (fType) params.append("event_type", fType);
    if (fSuccess !== "any") params.append("success", fSuccess);
    if (fIp) params.append("ip", fIp);
    if (fFrom) params.append("date_from", fFrom);
    if (fTo) params.append("date_to", fTo);
    params.append("limit", "200");
    try {
      const [list, sum, ty] = await Promise.all([
        axiosAuth().get(`/security/events?${params.toString()}`),
        axiosAuth().get("/security/events/summary"),
        axiosAuth().get("/security/events/types"),
      ]);
      setItems(list.data.items || []);
      setSummary(sum.data);
      setTypes(ty.data.types || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error cargando eventos");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const resetFilters = () => {
    setFEmail(""); setFType(""); setFSuccess("any"); setFIp(""); setFFrom(""); setFTo("");
  };

  const cards = useMemo(() => ([
    { id: "login_failed_24h", label: "Logins fallidos 24h", value: summary?.login_failed_24h ?? "—", icon: XCircle, color: "text-red-600" },
    { id: "account_locked_24h", label: "Bloqueos 24h", value: summary?.account_locked_24h ?? "—", icon: ShieldAlert, color: "text-orange-600" },
    { id: "login_success_24h", label: "Logins ok 24h", value: summary?.login_success_24h ?? "—", icon: CheckCircle2, color: "text-emerald-600" },
    { id: "password_reset_7d", label: "Resets 7 días", value: summary?.password_reset_7d ?? "—", icon: Shield, color: "text-blue-600" },
  ]), [summary]);

  return (
    <div className="space-y-4" data-testid="security-events-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="heading-industrial text-2xl sm:text-3xl font-bold text-slate-900 uppercase">Registro de Seguridad</h1>
          <p className="text-sm text-slate-500 mt-1">
            Auditoría de logins, cambios de contraseña, bloqueos y acciones sensibles.
          </p>
        </div>
        <Button onClick={load} variant="outline" className="text-xs uppercase tracking-wider rounded-sm" data-testid="sec-refresh-btn">
          <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Refrescar
        </Button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="sec-summary-cards">
        {cards.map(c => {
          const Ic = c.icon;
          return (
            <Card key={c.id} className="rounded-sm border-slate-200">
              <CardContent className="p-4 flex items-center justify-between">
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-slate-500">{c.label}</p>
                  <p className="text-2xl font-bold text-slate-900 mt-1" data-testid={`sec-kpi-${c.id}`}>{c.value}</p>
                </div>
                <Ic className={`w-6 h-6 ${c.color}`} />
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Filtros */}
      <Card className="rounded-sm border-slate-200">
        <CardContent className="p-4 space-y-3">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-slate-600">
            <Filter className="w-3.5 h-3.5" /> Filtros
          </div>
          <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
            <Input placeholder="Email" value={fEmail} onChange={(e) => setFEmail(e.target.value)} data-testid="sec-filter-email" className="rounded-sm" />
            <Select value={fType || "__all"} onValueChange={(v) => setFType(v === "__all" ? "" : v)}>
              <SelectTrigger className="rounded-sm" data-testid="sec-filter-type"><SelectValue placeholder="Evento" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all">Todos los eventos</SelectItem>
                {types.map(t => <SelectItem key={t} value={t}>{labelFor(t).label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={fSuccess} onValueChange={setFSuccess}>
              <SelectTrigger className="rounded-sm" data-testid="sec-filter-success"><SelectValue placeholder="Resultado" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="any">Cualquier resultado</SelectItem>
                <SelectItem value="true">Solo éxito</SelectItem>
                <SelectItem value="false">Solo error</SelectItem>
              </SelectContent>
            </Select>
            <Input placeholder="IP" value={fIp} onChange={(e) => setFIp(e.target.value)} data-testid="sec-filter-ip" className="rounded-sm" />
            <Input type="date" value={fFrom} onChange={(e) => setFFrom(e.target.value)} data-testid="sec-filter-from" className="rounded-sm" />
            <Input type="date" value={fTo} onChange={(e) => setFTo(e.target.value)} data-testid="sec-filter-to" className="rounded-sm" />
          </div>
          <div className="flex gap-2">
            <Button onClick={load} className="bg-slate-900 text-white uppercase text-xs tracking-wider h-8 rounded-sm" data-testid="sec-apply-filters">Aplicar</Button>
            <Button onClick={resetFilters} variant="ghost" className="text-xs uppercase tracking-wider h-8 rounded-sm" data-testid="sec-reset-filters">Limpiar</Button>
          </div>
        </CardContent>
      </Card>

      {/* Tabla */}
      <Card className="rounded-sm border-slate-200">
        <CardContent className="p-0">
          {loading ? (
            <p className="p-6 text-sm text-slate-400 text-center">Cargando…</p>
          ) : items.length === 0 ? (
            <p className="p-12 text-sm text-slate-400 text-center" data-testid="sec-empty">
              No hay eventos para los filtros seleccionados.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-slate-600 uppercase tracking-wider text-[10px]">
                  <tr>
                    <th className="p-2 text-left">Fecha</th>
                    <th className="p-2 text-left">Evento</th>
                    <th className="p-2 text-left">Email</th>
                    <th className="p-2 text-left">IP</th>
                    <th className="p-2 text-left">Motivo</th>
                    <th className="p-2 text-left">Actor</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {items.map(ev => {
                    const lbl = labelFor(ev.event_type);
                    return (
                      <tr key={ev.id} data-testid={`sec-row-${ev.id}`} className={`${ev.success ? "" : "bg-red-50/30"}`}>
                        <td className="p-2 text-slate-600 whitespace-nowrap">
                          {new Date(ev.created_at).toLocaleString("es-ES")}
                        </td>
                        <td className="p-2">
                          <Badge className={`${lbl.color} rounded-sm text-[10px] border`}>{lbl.label}</Badge>
                          {!ev.success && <span className="ml-1 text-red-500 text-[10px] uppercase">Error</span>}
                        </td>
                        <td className="p-2 text-slate-700">{ev.email || <span className="text-slate-300">—</span>}</td>
                        <td className="p-2 text-slate-500 font-mono text-[10px]">{ev.ip || "—"}</td>
                        <td className="p-2 text-slate-500">{ev.reason || (ev.details?.retry_after_seconds ? `bloqueo ${ev.details.retry_after_seconds}s` : "")}</td>
                        <td className="p-2 text-slate-500">{ev.actor_email || <span className="text-slate-300">—</span>}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
